import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
  ONBOARDING_STEPS,
  getOnboardingState,
  isOnboardingComplete,
  advanceStep,
  goToStep,
  skipOnboarding,
  syncOnboardingStateFromServer,
} from '@/config/onboardingSteps';

/**
 * First-login onboarding tour. Activates automatically when:
 *   1. User is logged in
 *   2. localStorage has no `smarto.onboarding.state.completed_at`/`skipped_at`
 *
 * Renders a spotlight + tooltip identical in style to the FeatureTourHighlight
 * but with sequential Next/Back navigation between steps. Navigates to
 * each step's `path` as needed.
 *
 * Public escape hatches: pressing Esc, clicking "Skip tour", or clicking
 * the close X all set `skipped_at` so the tour never re-shows.
 *
 * To restart manually: `restartOnboarding()` from /config/onboardingSteps.js.
 */
export const OnboardingTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [target, setTarget] = useState(null);
  const [rect, setRect] = useState(null);
  const findTimer = useRef(null);

  // Activate on mount if user is logged-in AND tour not yet completed.
  // Suppress when an ad-hoc feature tour is already in progress (?tour=...)
  // so the two overlays never render simultaneously. Pulls server-side
  // state so a tour completed on another device doesn't replay here.
  useEffect(() => {
    if (!user) { setActive(false); return; }
    const params = new URLSearchParams(window.location.search);
    if (params.get('tour')) { setActive(false); return; }

    let cancelled = false;
    (async () => {
      const server = await syncOnboardingStateFromServer();
      if (cancelled) return;
      const done = Boolean(
        (server && (server.completed_at || server.skipped_at)) || isOnboardingComplete()
      );
      if (done) { setActive(false); return; }
      const s = server || getOnboardingState();
      setStepIdx(s.step ?? 0);
      setActive(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const step = active ? ONBOARDING_STEPS[stepIdx] : null;

  // Navigate to the step's path if we're not on it
  useEffect(() => {
    if (!active || !step) return;
    if (location.pathname !== step.path) {
      navigate(step.path);
    }
  }, [active, stepIdx, step?.path]); // eslint-disable-line react-hooks/exhaustive-deps

  // Find the target DOM element
  useEffect(() => {
    if (!active || !step) { setTarget(null); return; }
    if (location.pathname !== step.path) return; // wait for nav
    if (findTimer.current) clearInterval(findTimer.current);
    let attempts = 0;
    const find = () => {
      const el = step.targetTestId
        ? document.querySelector(`[data-testid="${step.targetTestId}"]`)
        : null;
      if (el) {
        setTarget(el);
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
        clearInterval(findTimer.current);
        return true;
      }
      attempts += 1;
      if (attempts >= 12) clearInterval(findTimer.current);
      return false;
    };
    if (!find()) {
      findTimer.current = setInterval(find, 350);
    }
    return () => clearInterval(findTimer.current);
  }, [active, stepIdx, step?.targetTestId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track target's rect
  useEffect(() => {
    if (!target) { setRect(null); return; }
    const measure = () => {
      const r = target.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [target]);

  // Esc to skip
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === 'Escape') handleSkip(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleNext = () => {
    if (stepIdx >= ONBOARDING_STEPS.length - 1) {
      // Completion
      advanceStep(); // marks completed_at
      setActive(false);
      return;
    }
    advanceStep();
    setStepIdx((i) => i + 1);
  };

  const handleBack = () => {
    if (stepIdx <= 0) return;
    goToStep(stepIdx - 1);
    setStepIdx((i) => Math.max(0, i - 1));
  };

  const handleSkip = () => {
    skipOnboarding();
    setActive(false);
  };

  if (!active || !step) return null;

  // No target yet — show a centered "loading step" card so the user isn't
  // staring at a blank screen during navigation.
  if (!rect) {
    return (
      <div
        data-testid="onboarding-tour-loading"
        className="fixed inset-0 z-[85] bg-stone-900/40 backdrop-blur-[1px] flex items-center justify-center"
      >
        <div className="bg-white dark:bg-stone-800 rounded-xl shadow-2xl px-5 py-4 border border-stone-200 dark:border-stone-700">
          <p className="text-xs text-muted-foreground inline-flex items-center gap-2">
            <Sparkles size={12} className="text-orange-500" />
            Loading: {step.title}…
          </p>
        </div>
      </div>
    );
  }

  // Tooltip placement
  const PAD = 10;
  const TW = 360;
  const TH = 160;
  const placeBelow = (window.innerHeight - (rect.top + rect.height)) > TH + PAD * 2;
  const tooltipTop = placeBelow ? rect.top + rect.height + PAD : Math.max(PAD, rect.top - TH - PAD);
  const tooltipLeft = Math.max(
    PAD,
    Math.min(rect.left + rect.width / 2 - TW / 2, window.innerWidth - TW - PAD)
  );
  const isLast = stepIdx >= ONBOARDING_STEPS.length - 1 || step.isLast;

  return (
    <div
      data-testid="onboarding-tour-overlay"
      className="fixed inset-0 z-[85] pointer-events-none"
      role="dialog"
      aria-labelledby="onboarding-tour-title"
    >
      {/* Spotlight backdrop */}
      <div
        className="absolute inset-0 bg-stone-900/55 pointer-events-auto"
        onClick={handleSkip}
        data-testid="onboarding-tour-backdrop"
      />
      {/* Pulsing ring */}
      <div
        data-testid="onboarding-tour-ring"
        className="absolute rounded-2xl ring-4 ring-orange-500 ring-offset-2 ring-offset-white/0 shadow-2xl pointer-events-none animate-pulse"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
        }}
      />
      {/* Tooltip */}
      <div
        data-testid="onboarding-tour-tooltip"
        className="absolute pointer-events-auto bg-white dark:bg-stone-800 rounded-2xl shadow-2xl border border-stone-200 dark:border-stone-700 p-5"
        style={{ top: tooltipTop, left: tooltipLeft, width: TW }}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Sparkles size={13} className="text-white" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[9px] font-bold uppercase tracking-wider text-orange-600">
                Step {stepIdx + 1} of {ONBOARDING_STEPS.length}
              </span>
              <h4 id="onboarding-tour-title" className="text-sm font-bold font-outfit leading-tight truncate">
                {step.title}
              </h4>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSkip}
            data-testid="onboarding-tour-close-btn"
            className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-700 transition -mt-1 -mr-1 flex-shrink-0"
            aria-label="Skip tour"
          >
            <X size={13} />
          </button>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{step.hint}</p>
        {/* Progress dots */}
        <div className="flex gap-1.5 mt-3 mb-3" aria-hidden="true">
          {ONBOARDING_STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all ${i === stepIdx ? 'w-6 bg-orange-500' : i < stepIdx ? 'w-3 bg-orange-300' : 'w-3 bg-stone-200 dark:bg-stone-700'}`}
            />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleSkip}
            data-testid="onboarding-tour-skip-btn"
            className="text-[11px] text-muted-foreground hover:text-foreground transition"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {stepIdx > 0 && (
              <button
                type="button"
                onClick={handleBack}
                data-testid="onboarding-tour-back-btn"
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-stone-300 dark:border-stone-600 hover:bg-stone-50 dark:hover:bg-stone-700 transition inline-flex items-center gap-1"
              >
                <ChevronLeft size={12} /> Back
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              data-testid="onboarding-tour-next-btn"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition inline-flex items-center gap-1"
            >
              {step.cta || (isLast ? 'Finish' : 'Next')}
              {!isLast && <ChevronRight size={12} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
